/** SessionBoxesTab — Boxes tab content for SessionDetailsModal. Displays packer summary, packed boxes table with voided/cancelled section. */
import React from 'react';
import { Box, ChevronDown, Loader2, Printer, Users } from 'lucide-react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { formatDateTime, formatElapsedTime } from '../../../../utils/dateUtils';
import { formatGS1Barcode } from '../../../../utils/barcodeUtils';

interface SessionBoxesTabProps {
  session: any;
  isLoadingHistory: boolean;
  historyData: any;
  showAllCancelled: boolean;
  setShowAllCancelled: (v: boolean) => void;
  expandedDiscardedLabels: Set<string>;
  setExpandedDiscardedLabels: React.Dispatch<React.SetStateAction<Set<string>>>;
  setReprintBoxId: (id: string | null) => void;
}

const SessionBoxesTab: React.FC<SessionBoxesTabProps> = ({
  session,
  isLoadingHistory,
  historyData,
  showAllCancelled,
  setShowAllCancelled,
  expandedDiscardedLabels,
  setExpandedDiscardedLabels,
  setReprintBoxId,
}) => {
  const { t } = useLanguage();
  const [employeeSummaryOpen, setEmployeeSummaryOpen] = React.useState(false);

  return (
    <div className="space-y-6">
      {isLoadingHistory ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Packer Summary Section */}
          {historyData?.packerSummary && historyData.packerSummary.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div
                className={`flex items-center justify-between cursor-pointer select-none ${employeeSummaryOpen ? 'mb-4' : ''}`}
                onClick={() => setEmployeeSummaryOpen((v) => !v)}
              >
                <div className="flex items-center gap-2">
                  <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${employeeSummaryOpen ? 'rotate-180' : ''}`} />
                  <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('sessionDetails.employeeSummary')}
                  </h3>
                </div>
              </div>
              {employeeSummaryOpen && (
              <div className="space-y-2">
                {historyData.packerSummary.map((worker: { employee_number: string; role: string; worker_name: string; total_boxes: number; total_weight: number; total_elapsed_seconds: number }) => {
                  // Format elapsed time as "Xh Ym" or "Ym" if less than an hour
                  const totalSeconds = worker.total_elapsed_seconds || 0;
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const elapsedTime = hours > 0
                    ? `${hours}h ${minutes}m`
                    : `${minutes}m`;
                  const roleLabel = worker.role === 'operator'
                    ? t('sessionDetails.roleOperator')
                    : t('sessionDetails.rolePacker');

                  return (
                  <div
                    key={`${worker.employee_number}-${worker.role}`}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {worker.worker_name} <span className="text-gray-400 dark:text-gray-500 font-normal">({roleLabel})</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('sessionDetails.timeActive')}: {elapsedTime}
                      </span>
                      {worker.role === 'packer' && (
                        <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          {worker.total_boxes} {t('sessionDetails.cases').toLowerCase()}, {Number(worker.total_weight).toFixed(2)} lbs
                        </span>
                      )}
                    </div>
                  </div>
                );})}
              </div>
              )}
            </div>
          )}

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Box className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('sessionDetails.packedBoxes')} ({historyData?.boxes?.filter((b: { status: string }) => b.status === 'packed').length || 0})
              </h3>
            </div>
            {historyData?.sessionElapsedSeconds != null && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('sessionDetails.totalSessionTime')}: <span className="font-medium text-green-600 dark:text-green-400">{formatElapsedTime(historyData.sessionElapsedSeconds / 3600)}</span>
              </span>
            )}
          </div>
          {historyData?.boxes && historyData.boxes.length > 0 ? (
            (() => {
              // Group boxes by subsession
              type DiscardedLabel = { print_number: number; status: string; label_data: any; printed_at: string; printed_by: string; printed_by_name: string; discarded_at: string; discarded_by: string; discarded_by_name: string; discard_reason: string };
              type BoxType = { id: string; box_number: string; original_box_number?: string | null; barcode_number: string | null; original_barcode_number?: string | null; item_number: string | null; product_name: string | null; actual_weight: number; status: string; packed_at: string; created_at?: string | null; label_printed_at?: string | null; packed_by: string | null; packed_by_name: string | null; label_printed_by?: string | null; label_printed_by_name?: string | null; voided_at: string | null; voided_by: string | null; voided_by_name?: string | null; void_reason: string | null; lot_number: string | null; material_type: string | null; sub_session_number: string | null; station_name: string | null; total_prints?: number | null; discarded_labels?: DiscardedLabel[] | null };
              const allBoxesTyped = historyData.boxes as BoxType[];
              const boxesBySubsession = allBoxesTyped.reduce<Record<string, BoxType[]>>((acc, box) => {
                const key = box.sub_session_number || 'no-subsession';
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(box);
                return acc;
              }, {});

              const subsessionKeys = Object.keys(boxesBySubsession).sort();
              const hasMultipleSubsessions = subsessionKeys.length > 1 || (subsessionKeys.length === 1 && subsessionKeys[0] !== 'no-subsession');

              return (
                <div className="space-y-4">
                  {subsessionKeys.map((subsessionKey) => {
                    const boxes = boxesBySubsession[subsessionKey];
                    const firstBox = boxes[0];
                    const stationName = firstBox?.station_name || '-';
                    const packedBoxes = boxes.filter((b: BoxType) => b.status === 'packed');
                    const totalLbs = packedBoxes.reduce((sum: number, b: BoxType) => sum + Number(b.actual_weight || 0), 0);

                    return (
                      <div key={subsessionKey} className="border dark:border-gray-600 rounded-lg overflow-hidden">
                        {/* Subsession Header */}
                        {hasMultipleSubsessions && (
                          <div className="bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 border-b dark:border-gray-600">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-indigo-700 dark:text-indigo-300">
                                {subsessionKey === 'no-subsession'
                                  ? t('sessionDetails.noSubsession') || 'No Subsession'
                                  : `${t('sessionDetails.subsession') || 'Subsession'}: ${subsessionKey}`
                                }
                              </span>
                              <span className="text-sm text-indigo-600 dark:text-indigo-400">
                                {stationName !== '-' && stationName.replace(/-/g, ' ')}
                                <span className="ml-4">{packedBoxes.length} {packedBoxes.length === 1 ? 'case' : 'cases'} ({totalLbs.toFixed(2)} lbs)</span>
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Boxes Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
                                <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('sessionDetails.boxNumber')}</th>
                                <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('sessionDetails.barcode')}</th>
                                <th className="text-right py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('sessionDetails.weight')}</th>
                                <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('common.status')}</th>
                                <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('sessionDetails.updatedAt')}</th>
                                <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('sessionDetails.updatedBy')}</th>
                                <th className="text-center py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">{t('common.actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Separate voided/cancelled boxes from active boxes
                                const voidedCancelledBoxes = boxes.filter((b: BoxType) =>
                                  b.status === 'cancelled' || b.status === 'void' || b.status === 'voided'
                                );
                                const nonCancelledBoxes = boxes.filter((b: BoxType) =>
                                  b.status !== 'cancelled' && b.status !== 'void' && b.status !== 'voided'
                                );

                                // Collect discarded labels from non-voided boxes (reprints)
                                const discardedLabelsFromBoxes: Array<{ label: DiscardedLabel; box: BoxType }> = [];
                                nonCancelledBoxes.forEach((box: BoxType) => {
                                  if (box.discarded_labels && box.discarded_labels.length > 0) {
                                    box.discarded_labels.forEach(label => {
                                      discardedLabelsFromBoxes.push({ label, box });
                                    });
                                  }
                                });

                                const totalVoided = voidedCancelledBoxes.length + discardedLabelsFromBoxes.length;

                                const toggleDiscardedLabels = (boxId: string) => {
                                  setExpandedDiscardedLabels(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(boxId)) {
                                      newSet.delete(boxId);
                                    } else {
                                      newSet.add(boxId);
                                    }
                                    return newSet;
                                  });
                                };

                                const renderBoxRow = (box: BoxType) => {
                                  const hasReprints = (box.total_prints || 0) > 1;
                                  const hasDiscardedLabels = box.discarded_labels && box.discarded_labels.length > 0;
                                  const isExpanded = expandedDiscardedLabels.has(box.id);

                                  // Extract barcode from discarded label_data
                                  const getDiscardedBarcode = (labelData: any): string | null => {
                                    if (!labelData) return null;
                                    const data = typeof labelData === 'string' ? JSON.parse(labelData) : labelData;
                                    return data?.barcode || null;
                                  };

                                  return (
                                    <React.Fragment key={box.id}>
                                      <tr
                                        className={`border-b dark:border-gray-700 ${
                                          box.status === 'void' ? 'bg-red-50 dark:bg-red-900/20' : ''
                                        } ${hasDiscardedLabels ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
                                        onClick={hasDiscardedLabels ? () => toggleDiscardedLabels(box.id) : undefined}
                                      >
                                        <td className="py-2 px-2 text-gray-900 dark:text-white">
                                          <div className="flex items-center gap-2">
                                            #{box.box_number || box.original_box_number || '-'}
                                            {hasReprints && (box.total_prints || 0) > 2 && (
                                              <span className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                                                {(box.total_prints || 0) - 1}x
                                              </span>
                                            )}
                                            {hasDiscardedLabels && (
                                              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                              </svg>
                                            )}
                                          </div>
                                        </td>
                                        <td className={`py-2 px-2 font-mono text-xs ${box.status === 'cancelled' || box.status === 'void' || box.status === 'voided' ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'}`}>{formatGS1Barcode(box.barcode_number)}</td>
                                        <td className="py-2 px-2 text-right text-gray-900 dark:text-white">{Number(box.actual_weight).toFixed(2)} lbs</td>
                                        <td className="py-2 px-2">
                                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                            box.status === 'packed' && hasReprints ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                                            box.status === 'packed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                            (box.status === 'cancelled' || box.status === 'void' || box.status === 'voided') ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                            box.status === 'shipped' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                          }`}>
                                            {box.status === 'packed' && hasReprints ? 'reprinted' : box.status === 'cancelled' ? 'voided' : box.status}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 text-gray-600 dark:text-gray-300 text-xs">{formatDateTime(box.status === 'cancelled' || box.status === 'void' || box.status === 'voided' ? box.voided_at : box.status === 'pending' ? box.created_at : (box.label_printed_at || box.packed_at))}</td>
                                        <td className="py-2 px-2 text-gray-600 dark:text-gray-300">{(box.status === 'cancelled' || box.status === 'void' || box.status === 'voided' ? box.voided_by_name : box.status === 'pending' ? box.packed_by_name : (hasReprints ? box.label_printed_by_name : box.packed_by_name)) || '-'}</td>
                                        <td className="py-2 px-2 text-center">
                                          {/* Show reprint button only for packed, pending, or reprinted boxes */}
                                          {(box.status === 'packed' || box.status === 'pending' || hasReprints) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setReprintBoxId(box.id);
                                              }}
                                              className="p-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 transition-colors"
                                              title={t('supervisor.reprintLabel')}
                                            >
                                              <Printer className="w-4 h-4" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                      {/* Discarded labels rows */}
                                      {isExpanded && hasDiscardedLabels && box.discarded_labels?.map((label, idx) => (
                                        <tr key={`${box.id}-discarded-${idx}`} className="border-b dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10">
                                          <td className="py-1.5 px-2 pl-6 text-gray-500 dark:text-gray-400 text-xs">
                                            <span className="italic">#{label.print_number}</span>
                                          </td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 font-mono text-xs line-through">
                                            {formatGS1Barcode(getDiscardedBarcode(label.label_data))}
                                          </td>
                                          <td className="py-1.5 px-2 text-right text-gray-500 dark:text-gray-400 text-xs">{Number(box.actual_weight).toFixed(2)} lbs</td>
                                          <td className="py-1.5 px-2">
                                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                              discarded
                                            </span>
                                          </td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 text-xs">{formatDateTime(label.print_number === 1 ? label.printed_at : label.discarded_at)}</td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 text-xs">{label.print_number === 1 ? (label.printed_by_name || '-') : (label.discarded_by_name || '-')}</td>
                                          <td></td>
                                        </tr>
                                      ))}
                                      {/* Original barcode row (shown at the end if there are reprints, but only if no discarded label with print_number=1 exists) */}
                                      {isExpanded && hasReprints && box.original_barcode_number && !box.discarded_labels?.some(l => l.print_number === 1) && (
                                        <tr key={`${box.id}-original`} className="border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                                          <td className="py-1.5 px-2 pl-6 text-gray-500 dark:text-gray-400 text-xs">
                                            <span className="italic">#0</span>
                                          </td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 font-mono text-xs line-through">
                                            {formatGS1Barcode(box.original_barcode_number)}
                                          </td>
                                          <td className="py-1.5 px-2 text-right text-gray-500 dark:text-gray-400 text-xs">-</td>
                                          <td className="py-1.5 px-2">
                                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                              original
                                            </span>
                                          </td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 text-xs">{formatDateTime(box.packed_at)}</td>
                                          <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400 text-xs">{box.packed_by_name || '-'}</td>
                                          <td></td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                };

                                // Get most recent voided time for display (check both voided boxes and discarded labels)
                                const lastVoidedBoxTime = voidedCancelledBoxes.length > 0
                                  ? new Date(voidedCancelledBoxes[0].voided_at || voidedCancelledBoxes[0].created_at || '').getTime()
                                  : 0;
                                const lastDiscardedLabelTime = discardedLabelsFromBoxes.length > 0
                                  ? Math.max(...discardedLabelsFromBoxes.map(d => new Date(d.label.discarded_at || d.label.printed_at || '').getTime()))
                                  : 0;
                                const lastVoidedTime = lastVoidedBoxTime > lastDiscardedLabelTime
                                  ? (voidedCancelledBoxes.length > 0 ? formatDateTime(voidedCancelledBoxes[0].voided_at || voidedCancelledBoxes[0].created_at) : '')
                                  : (discardedLabelsFromBoxes.length > 0 ? formatDateTime(discardedLabelsFromBoxes.reduce((latest, d) => {
                                      const time = new Date(d.label.discarded_at || d.label.printed_at || '').getTime();
                                      return time > new Date(latest.label.discarded_at || latest.label.printed_at || '').getTime() ? d : latest;
                                    }).label.discarded_at || discardedLabelsFromBoxes[0].label.printed_at) : '');

                                return (
                                  <>
                                    {/* Non-cancelled/voided boxes */}
                                    {nonCancelledBoxes.map(renderBoxRow)}

                                    {/* Voided/Cancelled section - collapsible like Supervisor Override modal */}
                                    {totalVoided > 0 && (
                                      <tr>
                                        <td colSpan={7} className="p-0">
                                          <button
                                            onClick={() => setShowAllCancelled(!showAllCancelled)}
                                            className="w-full bg-red-50 dark:bg-red-900/20 border-y border-red-200 dark:border-red-800 px-3 py-2"
                                          >
                                            <div className="flex items-center justify-between">
                                              <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                                voided ({totalVoided})
                                              </span>
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-400">{lastVoidedTime}</span>
                                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${showAllCancelled ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                              </div>
                                            </div>
                                          </button>
                                        </td>
                                      </tr>
                                    )}

                                    {/* Expanded voided/cancelled boxes */}
                                    {showAllCancelled && voidedCancelledBoxes.map(renderBoxRow)}

                                    {/* Expanded discarded labels from reprints */}
                                    {showAllCancelled && discardedLabelsFromBoxes.map(({ label, box }, idx) => (
                                      <tr key={`discarded-${box.id}-${idx}`} className="border-b dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10">
                                        <td className="py-2 px-2 text-gray-500 dark:text-gray-400">
                                          #{box.box_number || box.original_box_number || '-'}
                                        </td>
                                        <td className="py-2 px-2 font-mono text-xs line-through text-gray-400 dark:text-gray-500">
                                          {formatGS1Barcode((() => {
                                            if (!label.label_data) return null;
                                            const data = typeof label.label_data === 'string' ? JSON.parse(label.label_data) : label.label_data;
                                            return data?.barcode || null;
                                          })())}
                                        </td>
                                        <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400">{Number(box.actual_weight).toFixed(2)} lbs</td>
                                        <td className="py-2 px-2">
                                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                            discarded
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 text-gray-500 dark:text-gray-400 text-xs">{formatDateTime(label.print_number === 1 ? label.printed_at : label.discarded_at)}</td>
                                        <td className="py-2 px-2 text-gray-500 dark:text-gray-400">{label.print_number === 1 ? (label.printed_by_name || '-') : (label.discarded_by_name || '-')}</td>
                                        <td></td>
                                      </tr>
                                    ))}
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              {t('sessionDetails.noCases')}
            </p>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default SessionBoxesTab;
